import { zodResolver } from '@hookform/resolvers/zod';
import { KeyRound, Send } from 'lucide-react';
import { useEffect, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { Link, useNavigate } from 'react-router-dom';

import { Button, Card, Input, OtpInput, SelectMenu, useToast } from '@/components/ui';
import { registerSchema, RegisterFormValues, ROLE_OPTIONS } from '@/features/auth/registerSchema';
import useAuth from '@/hooks/useAuth';
import { errorMessage } from '@/utils/apiError';
import { ROUTES } from '@/utils/constants';

// Mirrors the server's OTP_RESEND_COOLDOWN_SECONDS default. Unlike the login
// screen, /auth/register/send-otp answers a cooldown with a 429 carrying the
// remaining seconds in its message rather than a success body, so there is no
// value to read back here - this has to track the server setting by hand. Too
// high and the button stays dead after the server would already accept a resend.
const RESEND_SECONDS = 30;

const RegisterPage = () => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const {
    registerSendOtp,
    isSendingRegisterOtp,
    register: submitRegistration,
    isRegistering,
  } = useAuth();

  const {
    register,
    control,
    handleSubmit,
    getValues,
    formState: { errors },
  } = useForm<RegisterFormValues>({ resolver: zodResolver(registerSchema) });

  const [otpSent, setOtpSent] = useState(false);
  const [otp, setOtp] = useState('');
  const [otpError, setOtpError] = useState('');
  const [resendIn, setResendIn] = useState(0);

  useEffect(() => {
    if (!resendIn) return undefined;
    const timer = setTimeout(() => setResendIn((n) => Math.max(0, n - 1)), 1000);
    return () => clearTimeout(timer);
  }, [resendIn]);

  // Step 1: the full form is valid -> send the verification OTP to the mobile.
  const requestOtp = async (values: RegisterFormValues) => {
    try {
      await registerSendOtp(values.mobile);
      setOtpSent(true);
      setOtp('');
      setOtpError('');
      setResendIn(RESEND_SECONDS);
      toast.info(`OTP sent to +91 ${values.mobile}`);
    } catch (error) {
      toast.error(errorMessage(error, 'Could not send OTP. Please try again.'));
    }
  };

  // Step 2: submit the full profile + OTP -> create the pending account.
  const submitOtp = async (value: string = otp) => {
    if (value.length !== 6) {
      setOtpError('Enter the 6-digit OTP');
      return;
    }
    setOtpError('');
    const values = getValues();
    try {
      await submitRegistration({
        name: values.name,
        mobile: values.mobile,
        email: values.email,
        jobRole: values.role,
        otp: value,
      });
      toast.success('Registration submitted. An admin will review and approve your account.');
      void navigate(ROUTES.LOGIN);
    } catch (error) {
      setOtpError(errorMessage(error, 'Incorrect or expired OTP'));
    }
  };

  const editDetails = () => {
    setOtpSent(false);
    setOtp('');
    setOtpError('');
  };

  return (
    <div className="w-full max-w-2xl lg:max-w-lg xl:max-w-2xl">
      <Card padding="lg">
        <h1 className="text-2xl font-bold text-ink">Request Access</h1>
        <p className="mt-1.5 text-sm text-ink/60">
          Fill in your details and verify your mobile. An admin will review and approve your
          account.
        </p>

        <form
          onSubmit={(e) => {
            handleSubmit(requestOtp)(e).catch(() => {});
          }}
          noValidate
          className="mt-6 flex flex-col gap-4"
        >
          <div className="grid gap-4 sm:grid-cols-2 [&>*]:min-w-0">
            <Input
              id="name"
              label="Full name"
              type="text"
              autoComplete="name"
              placeholder="Your full name"
              error={errors.name?.message}
              disabled={otpSent}
              // eslint-disable-next-line react/jsx-props-no-spreading
              {...register('name')}
            />

            <div className="flex flex-col gap-1">
              {/* The Input below renders id="mobile", so htmlFor is a real
                  association; this rule's config here rejects the htmlFor-only form. */}
              {/* eslint-disable-next-line jsx-a11y/label-has-associated-control */}
              <label htmlFor="mobile" className="text-sm font-medium text-ink">
                Mobile number
              </label>
              <div className="flex items-start gap-2">
                <span className="flex h-11 shrink-0 items-center rounded-md border border-line bg-cream px-3 text-sm font-semibold text-ink/60 sm:h-9">
                  +91
                </span>
                <div className="min-w-0 flex-1">
                  <Input
                    id="mobile"
                    type="tel"
                    inputMode="numeric"
                    autoComplete="tel-national"
                    maxLength={10}
                    placeholder="10-digit mobile"
                    error={errors.mobile?.message}
                    disabled={otpSent}
                    className="w-full"
                    // eslint-disable-next-line react/jsx-props-no-spreading
                    {...register('mobile')}
                  />
                </div>
              </div>
            </div>

            <Input
              id="email"
              label="Work email"
              type="email"
              autoComplete="email"
              placeholder="you@advisorkhoj.com"
              error={errors.email?.message}
              disabled={otpSent}
              // eslint-disable-next-line react/jsx-props-no-spreading
              {...register('email')}
            />

            {/* The Radix SelectMenu, not the native form Select: a native
                <select> renders an OS-drawn, square option list that no
                stylesheet can round. This one draws its own panel, so the open
                menu matches the rest of the CRM (rounded panel, brand-filled
                selected row). */}
            <div className="flex flex-col gap-1">
              {/* A <span>, not a <label>: SelectMenu renders a button, which
                  htmlFor cannot target. The trigger carries its own aria-label. */}
              <span className="text-sm font-medium text-ink">Requested role</span>
              <Controller
                name="role"
                control={control}
                defaultValue=""
                render={({ field }) => (
                  <SelectMenu
                    ref={field.ref}
                    aria-label="Requested role"
                    // `undefined`, not `''`: SelectMenu maps the empty string to a
                    // private sentinel, and Radix only paints the placeholder
                    // for a genuinely unset value - passing '' through left the
                    // trigger blank with no placeholder text at all.
                    value={field.value || undefined}
                    onChange={(e) => field.onChange(e.target.value)}
                    disabled={otpSent}
                    placeholder="Select a role"
                    error={!!errors.role}
                    className="rounded-xl"
                  >
                    {ROLE_OPTIONS.map((role) => (
                      <option key={role} value={role}>
                        {role}
                      </option>
                    ))}
                  </SelectMenu>
                )}
              />
              {errors.role?.message && (
                <p className="text-xs text-danger" role="alert">
                  {errors.role.message}
                </p>
              )}
            </div>
          </div>

          {otpSent && (
            <div className="flex flex-col gap-1">
              <div className="flex items-center justify-between">
                <span className="text-sm font-medium text-ink">Enter OTP</span>
                <button
                  type="button"
                  disabled={resendIn > 0 || isSendingRegisterOtp}
                  onClick={() => {
                    handleSubmit(requestOtp)().catch(() => {});
                  }}
                  className="-my-2 -mr-2 inline-flex min-h-11 items-center rounded px-2 py-2 text-xs font-semibold text-brand hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand disabled:text-ink/40 disabled:no-underline"
                >
                  {resendIn > 0 ? `Resend in ${resendIn}s` : 'Resend OTP'}
                </button>
              </div>
              <OtpInput
                value={otp}
                onChange={setOtp}
                onComplete={(value) => {
                  void submitOtp(value);
                }}
                error={otpError}
                disabled={isRegistering}
              />
              <p className="-mb-2 mt-1 flex flex-wrap items-center gap-x-1 text-xs text-ink/60">
                {`Code sent to +91 ${getValues('mobile')}.`}
                <button
                  type="button"
                  onClick={editDetails}
                  className="-my-2 inline-flex min-h-11 items-center rounded px-1 font-semibold text-brand hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand"
                >
                  Edit details
                </button>
              </p>
            </div>
          )}

          {otpSent ? (
            <Button
              type="button"
              onClick={() => {
                void submitOtp();
              }}
              disabled={isRegistering}
              className="mt-2 min-h-11 w-full gap-1.5 sm:min-h-0"
            >
              <KeyRound className="h-4 w-4" /> Verify &amp; submit
            </Button>
          ) : (
            <Button
              type="submit"
              disabled={isSendingRegisterOtp}
              className="mt-2 min-h-11 w-full gap-1.5 sm:min-h-0"
            >
              <Send className="h-4 w-4" /> Send OTP
            </Button>
          )}
        </form>

        {/* `inline-flex` inside a flex row, not `inline-block` + `align-middle`:
            the link carries a 44px touch target, and as an inline-block that box
            is aligned by its own vertical-align against the text baseline, which
            dropped the label visibly below the sentence it belongs to. A flex
            line box centres the two against each other instead. */}
        <p className="-mb-2 mt-4 flex flex-wrap items-center justify-center gap-x-1 text-xs text-ink/60">
          Already registered?
          <Link
            to={ROUTES.LOGIN}
            className="-my-2 inline-flex min-h-11 items-center rounded px-1 font-semibold text-brand hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand"
          >
            Login
          </Link>
        </p>
      </Card>
    </div>
  );
};

export default RegisterPage;
